import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import Seo from './seo/Seo';
import ReadyToStartCTA from './components/ReadyToStartCTA';
import PageLoader from './components/PageLoader';
import AdminLayout from './components/AdminLayout';
import { AuthProvider } from './contexts/AuthContext';
import QueryProvider from './providers/QueryProvider';
import { normalizePathname, getCanonicalRoute } from './utils/urlNormalizer';

// Lazy load pages for code splitting and loading animations
const Home = lazy(() => import('./pages/Home'));
const Cards = lazy(() => import('./pages/Cards'));
const USDBankAccount = lazy(() => import('./pages/USDBankAccount'));
const StableCoinWallet = lazy(() => import('./pages/StableCoinWallet'));
const Contact = lazy(() => import('./pages/Contact'));
const About = lazy(() => import('./pages/About'));
const FAQ = lazy(() => import('./pages/FAQ'));
const Terms = lazy(() => import('./pages/Terms'));
const Privacy = lazy(() => import('./pages/Privacy'));
const CardTerms = lazy(() => import('./pages/CardTerms'));
const ProhibitedActivities = lazy(() => import('./pages/ProhibitedActivities'));
const ESignNotice = lazy(() => import('./pages/ESignNotice'));
const DbaDisclosure = lazy(() => import('./pages/sp/DbaDisclosure'));
const EndUserTerms = lazy(() => import('./pages/sp/EndUserTerms'));
const SpPrivacyPolicy = lazy(() => import('./pages/sp/SpPrivacyPolicy'));
const ConsumerProtectionPolicy = lazy(() => import('./pages/sp/ConsumerProtectionPolicy'));
const FraudSecurityPolicy = lazy(() => import('./pages/sp/FraudSecurityPolicy'));
const SpIndex = lazy(() => import('./pages/sp/SpIndex'));
const RequestRemoval = lazy(() => import('./pages/RequestRemoval'));
const Blog = lazy(() => import('./pages/Blog'));
const DownloadApp = lazy(() => import('./pages/DownloadApp'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Admin components - lazy loaded
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const RequestDetails = lazy(() => import('./pages/RequestDetails'));
const BlogManagement = lazy(() => import('./pages/BlogManagement'));
const BlogPostDetail = lazy(() => import('./pages/BlogPostDetail'));

// Scroll to top component
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// URL normalizer component - cleans invisible characters and normalizes routes
const URLNormalizer: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Get the actual browser URL pathname (may contain invisible characters)
    const browserPathname = window.location.pathname;
    const normalizedPath = normalizePathname(browserPathname);
    const canonicalRoute = getCanonicalRoute(normalizedPath);

    // If the browser path differs from the canonical route, redirect to the canonical route
    // Preserve query parameters and hash from the browser URL
    if (browserPathname !== canonicalRoute) {
      const search = window.location.search || '';
      const hash = window.location.hash || '';
      // Use replace to avoid adding to browser history
      navigate(canonicalRoute + search + hash, { replace: true });
    }
  }, [location.pathname, location.search, location.hash, navigate]);

  return null;
};

const App: React.FC = () => {
  return (
    <QueryProvider>
      <Router>
        <div className="min-h-screen bg-[#0A0F1C] font-inter">
          <Routes>
            {/* Admin routes */}
            <Route path="/admin/*" element={
              <AuthProvider>
                <AdminLayout>
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      <Route path="login" element={<AdminLogin />} />
                      <Route path="dashboard" element={<AdminDashboard />} />
                      <Route path="requests/:requestId" element={<RequestDetails />} />
                      <Route path="blog" element={<BlogManagement />} />
                    </Routes>
                  </Suspense>
                </AdminLayout>
              </AuthProvider>
            } />

            {/* Public routes */}
            <Route path="/*" element={
              <>
                <ScrollToTop />
                <URLNormalizer />
                <Seo />
                <Header />
                {/* Opaque plane that scrolls up to uncover the fixed footer */}
                <div className="site-shell">
                <main>
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/cards" element={<Cards />} />
                      <Route path="/accounts" element={<USDBankAccount />} />
                      <Route path="/usd-bank-account" element={<Navigate to="/accounts" replace />} />
                      <Route path="/eur-bank-account" element={<Navigate to="/accounts" replace />} />
                      <Route path="/stablecoin-wallet" element={<StableCoinWallet />} />

                      <Route path="/contact" element={<Contact />} />
                      <Route path="/about" element={<About />} />
                      <Route path="/faq" element={<FAQ />} />
                      <Route path="/blog" element={<Blog />} />
                      <Route path="/blog/:slug" element={<BlogPostDetail />} />
                      {/* Download app routes - handle variations and common typos */}
                      <Route path="/downloadapp" element={<DownloadApp />} />
                      <Route path="/download" element={<DownloadApp />} />
                      <Route path="/download-app" element={<DownloadApp />} />
                      <Route path="/download-apps" element={<DownloadApp />} />
                      <Route path="/downloadpage" element={<DownloadApp />} />
                      <Route path="/dowloadapp" element={<DownloadApp />} />
                      <Route path="/downloadap" element={<DownloadApp />} />
                      <Route path="/terms" element={<Terms />} />
                      <Route path="/privacy" element={<Privacy />} />
                      <Route path="/cards/cardpolicy" element={<CardTerms />} />
                      <Route path="/cards/prohibited-activities" element={<ProhibitedActivities />} />
                      <Route path="/cards/esign" element={<ESignNotice />} />
                      <Route path="/requestremoval" element={<RequestRemoval />} />
                      <Route path="/sp/dba-disclosure" element={<DbaDisclosure />} />
                      <Route path="/sp/end-user-terms" element={<EndUserTerms />} />
                      <Route path="/sp/privacy-policy" element={<SpPrivacyPolicy />} />
                      <Route path="/sp/consumer-protection-policy" element={<ConsumerProtectionPolicy />} />
                      <Route path="/sp/fraud-security-policy" element={<FraudSecurityPolicy />} />
                      <Route path="/sp" element={<SpIndex />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </main>
                <ReadyToStartCTA />
                </div>
                <Footer />
              </>
            } />
          </Routes>
        </div>
      </Router>
    </QueryProvider>
  );
};

export default App;
